import type { JSX, ReactNode } from 'react';
import { memo, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, chakra, CloseButton, Flex, Spinner, Text } from '@chakra-ui/react';

import type { ApiNotification } from '@izimi/api';
import { ApiRole } from '@izimi/api';
import { isUserRole, useModules } from '@izimi/modules';

import { NotificationTime } from './NotificationTime';

const Span = chakra('span');

type Props = {
  notification: Omit<ApiNotification, 'type'>;
  children?: ReactNode;
  messageValues?: Record<string, string | number>;
  messageKey?: string;
  Icon: ReactNode;
  messageComponents?: JSX.Element[];
  onDeleteNotification: () => void;
};

const NotificationListItem = ({
  notification,
  Icon,
  messageValues,
  messageKey,
  messageComponents,
  children,
  onDeleteNotification,
}: Props) => {
  const { timestamp } = notification;
  const { t } = useTranslation();
  const {
    notifications: { useDeleteNotification },
    user: { useCurrentUser },
  } = useModules();
  const { deleteNotification, isPending } = useDeleteNotification();
  const { currentUser } = useCurrentUser();

  const handleDeleteNotification = useCallback(async () => {
    await deleteNotification(notification.id);
    onDeleteNotification();
  }, [deleteNotification, notification.id, onDeleteNotification]);

  const components = () => {
    if (messageComponents) {
      return messageComponents;
    }
    if (messageValues) {
      return Object.keys(messageValues).map((m) => <Span fontWeight='600' key={m} />);
    }
    return undefined;
  };

  // Only citizens can dismiss actionable notifications
  const canDismiss =
    !children || (children && currentUser && isUserRole(ApiRole.Citizen, currentUser.roles));

  return (
    <Flex
      as='article'
      flexDirection='row'
      alignItems='stretch'
      justifyContent='center'
      py={3}
      px={1}
      mb={3}
      borderRadius='md'
      transition='0.2s'
      _hover={{ backgroundColor: 'neutral100' }}
      _focusWithin={{ backgroundColor: 'neutral100' }}>
      <Box ml={1}>{Icon}</Box>
      <Box flex={1} mx={3}>
        <Text as='h2' wordBreak='break-word'>
          <Trans i18nKey={messageKey} values={messageValues} components={components()} t={t} />
        </Text>
        <NotificationTime timestamp={timestamp} />
        {children}
      </Box>
      <Flex alignItems='start'>
        {isPending ? (
          <Spinner color='primary300' size='sm' p={1} mt={2} mr={4} />
        ) : (
          canDismiss && (
            <CloseButton
              color='neutral300'
              onClick={handleDeleteNotification}
              title={t('notifications:dismiss')}
              aria-label={t('notifications:dismiss')}
              mr={2}
            />
          )
        )}
      </Flex>
    </Flex>
  );
};

export default memo(NotificationListItem);
